import { Box, CircularProgress } from "@mui/material";
import AccordionBox from "components/Layouts/AccordionBox";
import { getLocalStorageData, isoDateWithoutTimeZone } from "helpers/utils";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { showError } from "../../../../redux/common/actions";
import {
	getMissingPartsTools,
	getPauses,
	getSkippedTasks,
	getStatusChanges,
	getStops,
} from "services/services/impacts";
import CommonTable from "./CommonTable";
import { dateDifference } from "helpers/utils";
import { serviceStatus } from "constants/serviceDetails";
import { getSiteApplicationDetail } from "services/clients/sites/siteApplications/siteApplicationDetails";
import updateStorage from "helpers/updateStorage";
import i18n from "i18n";
import ActionButtonStyle from 'styles/application/ActionButtonStyle';
import { makeStyles } from "tss-react/mui";

const AT = ActionButtonStyle();

const useStyles = makeStyles()((theme) => ({
	goToTopButton: {
		"&.MuiButton-root": {
		backgroundColor: "white",
		border: "1px solid #D2D2D9",
		color: "#679ced",

		
	},"&:hover":{backgroundColor:'#f8f8f8!important'} },
}));


const getAllData = (customCaptions, application) => [
	{
		id: 1,
		title: customCaptions?.pauseReasonPlural ?? "Pause Reasons",
		data: [],
		columns: [
			"userName",
			"startDate",
			"endDate",
			"pauseMinutes",
			"pauseReasonName",
			"pauseSubcategoryName",
			"otherReason",
		],
		headers: [
			{ key: "User", label: customCaptions?.user ?? "User" },
			{ key: "Start Time", label: i18n.t("label.startTime") },
			{ key: "End Time", label: i18n.t("label.endTime") },
			{ key: "Total Duration", label: i18n.t("label.totalDuration") },
			{ key: "Reason", label: i18n.t("label.reason") },
			{ key: "Pause Subcategory", label: i18n.t("label.pauseSubcategory") },
			{ key: "Other Reason", label: i18n.t("label.otherReason") },
		],
	},
	{
		id: 2,
		title: customCaptions?.stopReasonPlural ?? "Stop Reasons",
		data: [],
		columns: ["displayName", "endDate", "stopReason", "otherReason"],
		headers: [
			{ key: "User", label: customCaptions?.user ?? "User" },
			{ key: "End Time", label: i18n.t("label.endTime") },
			{ key: "Reason", label: i18n.t("label.reason") },
			{ key: "Other Reason", label: i18n.t("label.otherReason") },
		],
	},
	{
		id: 3,
		title: application.showParts
			? i18n.t("label.missingPartsAndTools", {
					parts: customCaptions?.partPlural ?? "Parts",
					tools: customCaptions?.toolPlural ?? "Tools",
			  })
			: i18n.t("label.missingEntity", {
					entity: customCaptions?.toolPlural ?? "Tools",
			  }),
		data: [],
		columns: ["userName", "dateTime", "partOrTool", "reasonName", "otherReason"],
		headers: [
			{ key: "User", label: customCaptions?.user ?? "User" },
			{ key: "Time", label: i18n.t("label.time") },
			{
				key: "partTool",
				label: `${customCaptions?.part ?? "part"} / ${
					customCaptions?.tool ?? "tool"
				}`,
			},
			{ key: "Reason", label: i18n.t("label.reason") },
			{ key: "Other Reason", label: i18n.t("label.otherReason") },
		],
	},
	{
		id: 4,
		title: i18n.t("label.skippedEntity", {
			entity: customCaptions?.taskPlural ?? "Tasks",
		}),
		data: [],
		columns: [
			"skippedByUser",
			"stageName",
			"zoneName",
			"taskActionName",
			"skipReasonName",
			"otherReason",
		],
		headers: [
			{ key: "User", label: customCaptions?.user ?? "User" },
			{ key: "stage", label: customCaptions?.stage },
			{ key: "zone", label: customCaptions?.zone },
			{ key: "task", label: customCaptions?.task },
			{ key: "Reason", label: i18n.t("label.reason") },
			{ key: "Other Reason", label: i18n.t("label.otherReason") },
		],
	},
	{
		id: 5,
		title: i18n.t("label.statusChanges"),
		data: [],
		columns: [
			"userName",
			"createdDateTime",
			"status",
			"changeStatusReasonName",
			"otherReason",
		],
		headers: [
			{ key: "User", label: customCaptions?.user ?? "User" },
			{ key: "Time", label: i18n.t("label.time") },
			{ key: "New Status", label: i18n.t("label.newStatus") },
			{ key: "Reason", label: i18n.t("label.reason") },
			{ key: "Other Reason", label: i18n.t("label.otherReason") },
		],
	},
];

function Impacts() {
	const { classes } = useStyles();
	const { t } = useTranslation();

	const { id } = useParams();
	const { application, customCaptions, siteAppID } =
		JSON.parse(sessionStorage.getItem("me")) ||
		JSON.parse(localStorage.getItem("me"));
	const [data, setData] = useState(getAllData(customCaptions, application));
	const [loading, setLoading] = useState(true);
	const [showGoTop, setShowGoTop] = useState(false);
	const dispatch = useDispatch();

	const [siteAppState, setSiteAppState] = useState({ application });
	const reduxDispatch = useDispatch();

	useEffect(() => {
		const handleScroll = () => {
			setShowGoTop(window.scrollY > 300);
		};

        window.addEventListener('scroll', handleScroll);

        return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	const fetchSiteApplicationDetails = async () => {
		try {
			const result = await getSiteApplicationDetail(siteAppID);
			setSiteAppState(result?.data);
		} catch (error) {
			reduxDispatch(showError(error?.response?.data || t("message.somethingWentWrong")));
		}
	};
	const fetchAllData = useCallback(async () => {
		const response = await Promise.all([
			getPauses(id),
			getStops(id),
			getMissingPartsTools(id),
			getSkippedTasks(id),
			getStatusChanges(id),
		]);

		if (response.some((res) => !res.status)) {
			const errorTileIndex = response.findIndex((res) => !res.status);
			dispatch(
				showError(
					response?.[errorTileIndex].data?.detail || t("message.couldNotFetchData")
				)
			);
		}
		const pause = response[0].data
			? response[0].data.map((pause) => ({
					...pause,
					pauseMinutes: dateDifference(pause.endDate, pause.startDate),
					startDate: isoDateWithoutTimeZone(pause.startDate + "Z"),
					endDate: isoDateWithoutTimeZone(pause.endDate + "Z"),
			  }))
			: [];
		const stop = response[1].data
			? response[1].data.map((stop) => ({
					...stop,

					endDate: isoDateWithoutTimeZone(stop.endDate + "Z"),
			  }))
			: [];
		const missingPartTool = response[2].data
			? response[2].data.map((part) => ({
					...part,
					partOrTool: (part.partName || part.toolName) ?? "",
					dateTime: isoDateWithoutTimeZone(part.dateTime + "Z"),
			  }))
			: [];

		const skippedTasks = response[3].data
			? response[3].data.map((skip) => ({
					...skip,
					taskActionName: `${skip?.actionName} ${skip?.taskName}`,
			  }))
			: [];

		const status = response[4].data
			? response[4].data.map((status) => ({
					...status,
					createdDateTime: isoDateWithoutTimeZone(status.createdDateTime + "Z"),
					status: serviceStatus[status.status],
			  }))
			: [];

		setData((prev) => [
			{ ...prev[0], data: pause },
			{ ...prev[1], data: stop },
			{ ...prev[2], data: missingPartTool },
			{ ...prev[3], data: skippedTasks },
			{ ...prev[4], data: status },
		]);
		setLoading(false);
	}, [dispatch, id]);

	useEffect(() => {
		fetchAllData();
	}, [fetchAllData]);

	useEffect(() => {
		fetchSiteApplicationDetails();
		if (siteAppID) updateStorage(siteAppID);
	}, []);
	if (loading) {
		return <CircularProgress />;
	}

	const handleGoToTop = () => {
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}
 
	return (
		<div style={{ margin: "30px 0" }}>
			{data.map((element) => (
				<AccordionBox
					key={element.id}
					title={`${element.title} (${element.data?.length ?? 0})`}
					style={{ margin: "20px 0" }}
				>
					<CommonTable
						data={element.data}
						columns={element.columns}
						headers={element.headers}
					/>
				</AccordionBox>
			))}
			{showGoTop && (
				<Box sx={{ textAlign: 'center', my: 3 }}>
					<AT.GeneralButton
								className={classes.goToTopButton}
								onClick={handleGoToTop}
							>
								{t("action.goToTop")}
					</AT.GeneralButton>
				</Box>
			)}
		</div>
	);
}

export default Impacts;
